import { computed, ref } from 'vue'
import { MembershipTier, PlanType } from '@/shared/graphql/legacy/generated'

const STORAGE_KEY = 'dacrPricingCheckout'

export type PricingCheckoutStage = 'checkout' | 'onboarding'

export interface PricingCheckoutState {
  stage: PricingCheckoutStage
  productId: string
  membershipTier?: MembershipTier
  planType?: PlanType
}

const readStoredState = (): PricingCheckoutState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const value = JSON.parse(raw) as Partial<PricingCheckoutState>

    if (value.stage !== 'onboarding') {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    if (typeof value.productId !== 'string' || !value.productId) return null

    return {
      stage: value.stage,
      productId: value.productId,
      ...(value.membershipTier &&
        Object.values(MembershipTier).includes(value.membershipTier) && {
          membershipTier: value.membershipTier,
        }),
      ...(value.planType &&
        Object.values(PlanType).includes(value.planType) && {
          planType: value.planType,
        }),
    }
  } catch {
    return null
  }
}

const state = ref<PricingCheckoutState | null>(readStoredState())

const write = (value: PricingCheckoutState | null) => {
  state.value = value

  if (value?.stage === 'onboarding') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export const pricingCheckout = computed(() => state.value)

export const startPricingCheckout = (
  productId: string,
  membershipTier?: MembershipTier | null
) => {
  write({
    stage: 'checkout',
    productId,
    ...(membershipTier && { membershipTier }),
  })
}

export const markPricingCheckoutPurchased = (
  planType?: PlanType,
  membershipTier?: MembershipTier | null
) => {
  if (!state.value) return

  write({
    ...state.value,
    stage: 'onboarding',
    ...(planType && { planType }),
    ...(membershipTier && { membershipTier }),
  })
}

export const clearPricingCheckout = () => {
  if (!state.value) return

  write(null)
}
